<template>
  <editor
    ref="editor"
    v-if="instance"
    v-model="instance"
    :is-new="isNew"
    :disabled="!editing"
    :errors="validationErrors"
    @cancel="cancel"
    @edit="edit"
    @removed="removed"
    @remove-error="handleError"
    @reset-errors="resetErrors"
    @saved="saved"
    @save-error="handleError"
    @load="load"
  >
    <div slot="buttons">
      <!--
      ==========================================================================
      Unsatisfied tag alternatives modal (shown on save)
      ==========================================================================
      -->
      <full-page-modal
        ref="unsatisfiedTagAlternativesModal"
        title="Non puoi andare nel prossimo stato senza caricare i seguenti tag"
        header-bg-variant="danger"
        header-class="text-white"
        body-class="p-0"
        ok-only
      >
        <base-status-tag-alternatives-form
          :value="{ tagAlternatives: unsatisfiedTagAlternatives }"
          disabled
        />
      </full-page-modal>

      <!--
      ==========================================================================
      Buttons > Cancel
      ==========================================================================
      -->
      <b-button variant="cancel" class="float-left" @click.prevent="cancel()">
        <i class="fa fa-fw fa-arrow-left"></i>
        <span class="d-sm-inline d-none">{{ $t("common.cancel") }}</span>
      </b-button>

      <!--
      ==========================================================================
      Buttons > Actions
      ==========================================================================
      -->
      <b-button variant="primary" @click.prevent="$refs.actionsSheet.show()">
        <i class="fa fa-fw fa-ellipsis-v"></i>
        <span class="d-sm-inline d-none">{{ $t("common.actions") }}</span>
      </b-button>

      <!--
      ==========================================================================
      Buttons > Actions Sheet
      ==========================================================================
      -->
      <modal-sheet ref="actionsSheet">
        <b-button
          v-if="instance.permissions.saveInitial && !editing"
          block
          variant="warning"
          @click.prevent="
            edit();
            $refs.actionsSheet.hide();
          "
        >
          {{ $t("common.edit") }}
        </b-button>

        <b-button
          v-if="instance.permissions.changeStatus && auspicableNextStatus"
          variant="success"
          block
          @click.prevent="goToStatus(auspicableNextStatus.toStatus)"
        >
          {{ auspicableNextStatus.label }}
        </b-button>
        <b-button
          v-if="instance.permissions.changeStatus && sideStatus"
          variant="warning"
          block
          @click.prevent="goToStatus(sideStatus.toStatus)"
        >
          {{ sideStatus.label }}
        </b-button>
        <b-button
          v-if="instance.permissions.saveInitial && editing"
          variant="warning"
          block
          @click.prevent="saveInitial()"
        >
          {{ $t("file.actions.saveInitial") }}
        </b-button>
        <b-button
          v-if="instance.permissions.changeStatus && failureStatus"
          variant="danger"
          block
          @click.prevent="goToStatus(failureStatus.toStatus)"
        >
          {{ failureStatus.label }}
        </b-button>

        <b-button
          block
          variant="cancel"
          @click.prevent="$refs.actionsSheet.hide()"
        >
          {{ $t("common.cancel") }}
        </b-button>
      </modal-sheet>

      <!--
      ==========================================================================
      Buttons > Debug
      ==========================================================================
      -->
      <b-button v-if="!$env.production" variant="dark" v-b-modal.modal-debug>
        <i class="fa fa-fw fa-code"></i>
        <span class="d-sm-inline d-none">{{ $t("common.debug") }}</span>
      </b-button>

      <!--
      ==========================================================================
      Buttons > Remove
      ==========================================================================
      -->
      <b-button
        v-if="instance.permissions.delete"
        variant="remove"
        @click.prevent="$refs.removeSheet.show()"
      >
        <i class="fa fa-fw fa-times"></i>
        <span class="d-sm-inline d-none">{{ $t("common.remove") }}</span>
      </b-button>

      <!--
      ==========================================================================
      Buttons > Remove Sheet
      ==========================================================================
      -->
      <modal-sheet ref="removeSheet">
        <b-button block variant="danger" @click.prevent="remove()">
          {{ $t("common.remove") }}
        </b-button>
        <b-button
          block
          variant="cancel"
          @click.prevent="$refs.removeSheet.hide()"
        >
          {{ $t("common.cancel") }}
        </b-button>
      </modal-sheet>
    </div>
  </editor>
</template>
<script>
import LoadPageMixin from "vue-template/protected/mixins/LoadPageMixin";
import pluralize from "pluralize";
import lowerFirst from "lodash/lowerFirst";
import { uuid } from "core/utils";
import BaseStatusTagAlternativesForm from "@/modules/core/product/protected/BaseStatusTagAlternativesForm";

export default {
  mixins: [LoadPageMixin],

  components: {
    BaseStatusTagAlternativesForm
  },

  data() {
    return {
      unsatisfiedTagAlternatives: null,
      pendingFiles: []
    };
  },

  computed: {
    outgoingArrows() {
      const s = this.instance && this.instance.status;
      if (!s) return [];
      const ret = s.outgoingArrows.map(a => ({
        label: a.label,
        toStatus: this.instance.product.statuses.find(
          n => n.id === a.toStatusId
        )
      }));
      //console.log('outgoingArrows', ret);
      return ret;
    },

    auspicableNextStatus() {
      return this.outgoingArrows.find(x => x.toStatus.colPosition === 1);
    },

    sideStatus() {
      return this.outgoingArrows.find(
        x => x.toStatus.colPosition === 2 && x.toStatus.type === "INTERMEDIATE"
      );
    },

    failureStatus() {
      return this.outgoingArrows.find(x => x.toStatus.type === "FAILURE");
    }
  },

  methods: {
    getService() {
      throw Error("Virtual method");
    },

    getLabel(/*instance*/) {
      throw Error("Virtual method");
    },

    breadcrumbs() {
      const routeName = this.$route.name;

      return [
        {
          icon: "home",
          text: "Home",
          to: "/protected"
        },
        {
          text: () => this.$t("menu." + lowerFirst(pluralize(routeName))),
          to: { name: pluralize(routeName) }
        },
        {
          text:
            this.$route.params.id === "new"
              ? "<New>"
              : this.getLabel(this.instance) || "<New>"
        }
      ];
    },

    edit() {
      const params = { id: this.instance.id };
      const query = { edit: true };
      this.$router.push({ name: this.$route.name, params, query });
    },

    cancel() {
      this.$router.push({ name: pluralize(this.$route.name) });
    },

    removed(/*instance*/) {
      this.$router.push({ name: pluralize(this.$route.name) });
    },

    async saved(instance) {
      const params = { id: instance.id, edit: "false" };
      await this.$router.push({
        name: this.$route.name,
        params,
        query: { rnd: uuid() }
      });
    },

    variantFor(s) {
      switch (s.type) {
        case "INTERMEDIATE":
          return "warning";
        case "SUCCESS":
          return "success";
        case "FAILURE":
          return "danger";
      }
      throw Error("Unsupported type");
    },

    nextActorFor(primaryActor) {
      switch (primaryActor) {
        case "ADMIN":
          return "all'amministratore di sistema";
        case "CUSTOMER":
          return "al cliente";
        case "PROVIDER":
          return "al fornitore";
        case "BROKER":
          return "al gestore";
        default:
          throw Error("Unsupported: " + primaryActor);
      }
    },

    async saveInitial() {
      await this.$refs.editor.save();
      this.$refs.actionsSheet.hide();
    },

    async goToStatus(s) {
      let nextActor = s.primaryActor.map(this.nextActorFor).join(" o ");
      const msg =
        "Questa azione manderà la pratica in stato " +
        s.label +
        " dove il controllo passerà " +
        nextActor +
        ". Sei sicuro di voler procedere?";

      if (!confirm(msg)) {
        return;
      }

      this.$refs.actionsSheet.hide();
      return await this.$refs.editor.save({
        status: s
      });
    },

    async onError428({ statusCode, data }) {
      console.error("onError428", statusCode, data);
      this.unsatisfiedTagAlternatives = data;
      this.$refs.unsatisfiedTagAlternativesModal.show();
    }
  }
};
</script>
